/**
 * Стор настроек приложения — загрузка и обновление глобальных параметров
 *
 * Основные функции:
 * - Загрузка настроек с сервера (GetSettings — публичный, без accessToken)
 * - Обновление настроек через UpdateSettings (требует accessToken)
 */

// Фабрика определения Pinia стора
import { defineStore } from 'pinia'
// Обёртка gRPC-вызовов с обработкой токенов и ошибок
import { grpcCall } from '@/grpc-client/grpc/grpc-call'
// Фабрика gRPC-клиентов для всех эндпоинтов
import { $grpcClient } from '@/grpc-client/grpc-client'
// Инъекция accessToken в gRPC-запросы
import { grpcReq } from '@/grpc-client/grpc/grpc-request'
// Логгер событий с отправкой ошибок на бэкенд
import { EventLogger } from '@/utils/EventLogger'
import { grpcErrorMessage } from '@/utils/grpcErrorMessage'
// Стор обратной связи — глобальный лоадер и тост-уведомления
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'
// Типизация payload для обновления настроек
import type { IUpdateSettingsPayload } from '@/modules/settings/types/IUpdateSettingsPayload'

export const useSettingsStore = defineStore('settingsStore', {
  state: () => ({
    // Username бота поддержки в Telegram
    supportBotUsername: '',
    // ID Telegram-бота для OIDC
    telegramBotId: 0n,
    // Флаг отображения главного логотипа приложения (false до загрузки settings)
    showMainLogo: false,
    // Флаг отображения глобального объявления (тех. работы)
    showGlobalNotification: false,
    // Текст глобального объявления о тех. работах
    notificationText: '',
    // Email поддержки
    supportEmail: '',
    // Список URL прокси-серверов
    proxyURLs: [] as string[],
    // Флаг завершённости первичной загрузки настроек с сервера
    isLoaded: false,
  }),
  actions: {
    /**
     * Загружает настройки приложения с сервера
     * GetSettings — публичный эндпоинт, не требует accessToken
     *
     * @returns {Promise<boolean>} Успешность загрузки
     */
    async getSettings() {
      try {
        // Публичный эндпоинт — передаём пустой объект без accessToken
        const [ok, response] = await grpcCall(
          (_args, options) =>
            $grpcClient.SettingsEndpoint.getSettings({}, options),
          'getSettings',
        )

        if (!ok || !response || response.error) {
          EventLogger.error('getSettings', ['Не удалось загрузить настройки', response?.error])
          return false
        }

        if (response.settings) {
          this.supportBotUsername = response.settings.supportBotUsername
          this.telegramBotId = response.settings.telegramBotId
          this.showMainLogo = response.settings.showMainLogo
          this.showGlobalNotification = response.settings.showGlobalNotification
          this.notificationText = response.settings.notificationText
          this.supportEmail = response.settings.supportEmail
          this.proxyURLs = response.settings.proxyURLs
        }
        this.isLoaded = true
        return true
      } catch (error) {
        EventLogger.error('getSettings', [`Catch: ${(error as Error).message}`])
        return false
      }
    },

    /**
     * Обновляет настройки приложения на сервере
     * Требует accessToken (добавляется через grpcReq)
     *
     * @param {IUpdateSettingsPayload} data - Частичный объект настроек для обновления
     * @returns {Promise<boolean>} Успешность обновления
     */
    async updateSettings(data: IUpdateSettingsPayload) {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true

      try {
        const [ok, response] = await grpcCall(
          (_args, options) =>
            $grpcClient.SettingsEndpoint.updateSettings(
              grpcReq({ ...data, proxyURLs: data.proxyURLs ?? this.proxyURLs }),
              options,
            ),
          'updateSettings',
        )

        if (response?.error) {
          EventLogger.error('updateSettings', ['Ошибка обновления настроек', response?.error])
          feedbackStore.showToast({
            type: 'error',
            message: grpcErrorMessage(response.error, 'errors.tryLater'),
          })
          return false
        }

        if (!ok) {
          EventLogger.error('updateSettings', ['Не удалось обновить настройки'])
          return false
        }

        // Синхронизация локального стейта с переданными значениями
        if (data.supportBotUsername !== undefined) this.supportBotUsername = data.supportBotUsername
        if (data.telegramBotId !== undefined) this.telegramBotId = data.telegramBotId
        if (data.showMainLogo !== undefined) this.showMainLogo = data.showMainLogo
        if (data.showGlobalNotification !== undefined)
          this.showGlobalNotification = data.showGlobalNotification
        if (data.notificationText !== undefined) this.notificationText = data.notificationText
        if (data.supportEmail !== undefined) this.supportEmail = data.supportEmail
        if (data.proxyURLs !== undefined) this.proxyURLs = data.proxyURLs

        feedbackStore.showToast({ type: 'success', message: 'Настройки сохранены' })
        return true
      } catch (error) {
        EventLogger.error('updateSettings', [`Catch: ${(error as Error).message}`])
        return false
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },
  },
})
